import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import type { ConfigReferentiel } from "../../../../convex/referentiel/validation";
import type { StatFourchette } from "../../../../convex/referentiel/agregation";
import { libelleAttentePourCible } from "./fourchetteLibelles";

type Props = {
  config: ConfigReferentiel;
  echelle: { min: number; max: number };
  /** Agrégat de la version courante : `code` → cible moyenne des référents. */
  agregat: Record<string, StatFourchette>;
};

/**
 * Le référentiel PAMI lui-même — l'équivalent de `ReferentielPyramide` pour
 * l'échelle fourchette.
 *
 * Sans lui, un référentiel PAMI configuré et calculé restait INVISIBLE : l'onglet
 * n'annonçait que sa version et la liste des dimensions, jamais l'attente. On ne
 * pouvait relire « ce contre quoi les candidats sont comparés » qu'en ouvrant la
 * contribution du référent, dans un autre onglet.
 *
 * La zone attendue est dessinée sur l'échelle, pas seulement chiffrée : c'est la
 * position ET la largeur qui se lisent d'un coup d'œil, alors que « 7,0 – 10,0 »
 * demande de reconstruire mentalement l'échelle à chaque ligne. L'intensité code
 * le poids, comme sur le graphique de matching candidat : uniforme pour une
 * contribution à attente unique, plus pâle pour une dimension « important »
 * d'un référentiel historique.
 */
export function ReferentielFourchetteCard({
  config,
  echelle,
  agregat,
}: Props) {
  const amplitude = echelle.max - echelle.min;
  const groupes = [
    { cle: "motivation", titre: "Motivations" },
    { cle: "personnalite", titre: "Personnalité (HEXACO)" },
  ] as const;

  // Les dimensions sans catégorie (config sur mesure) forment un dernier groupe
  // sans titre plutôt que de disparaître.
  const sansCategorie = config.dimensions.filter(
    (d) => d.categorie !== "motivation" && d.categorie !== "personnalite",
  );

  const ligne = (dimension: (typeof config.dimensions)[number]) => {
    const stat = agregat[dimension.code];
    return (
      <li
        key={dimension.code}
        className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3 text-sm"
      >
        <span className="min-w-40 flex-1">{dimension.label}</span>

        {stat ? (
          <>
            {/* Échelle 0-10 avec la zone attendue en surimpression. */}
            <span
              className="relative h-2 w-40 shrink-0 rounded-full bg-muted"
              role="img"
              aria-label={`Zone attendue de ${stat.min.toFixed(1)} à ${stat.max.toFixed(1)} sur ${echelle.min}–${echelle.max}`}
            >
              <span
                className="absolute inset-y-0 rounded-full bg-primary"
                style={{
                  left: `${((stat.min - echelle.min) / amplitude) * 100}%`,
                  width: `${Math.max(2, ((stat.max - stat.min) / amplitude) * 100)}%`,
                  opacity: opaciteImportance(stat.poids),
                }}
              />
            </span>
            <span className="w-20 shrink-0 text-right tabular-nums text-muted-foreground">
              {stat.min.toFixed(1)} – {stat.max.toFixed(1)}
            </span>
            <Badge variant="outline" className="shrink-0">
              {libelleAttentePourCible(stat, echelle)}
            </Badge>
          </>
        ) : (
          // Dimension sans attente particulière pour TOUS les référents :
          // absente de l'agrégat, donc hors du score.
          <span className="text-muted-foreground">
            Pas d'attente particulière — hors du calcul de matching
          </span>
        )}
      </li>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Attentes du référentiel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Zone de score attendue chez le candidat sur l'échelle {echelle.min}–
          {echelle.max} : score bas (0 à 3), moyen (4 à 6) ou élevé (7 à 10).
          Plus le candidat s'éloigne de la zone, plus il perd de points,
          d'abord vite puis de moins en moins.
        </p>

        {groupes.map(({ cle, titre }) => {
          const dimensions = config.dimensions.filter(
            (d) => d.categorie === cle,
          );
          if (dimensions.length === 0) return null;
          return (
            <div key={cle} className="space-y-1">
              <p className="text-sm font-medium">
                {titre}{" "}
                <span className="text-muted-foreground">
                  ({dimensions.length})
                </span>
              </p>
              <ul className="divide-y rounded-surface border">
                {dimensions.map(ligne)}
              </ul>
            </div>
          );
        })}

        {sansCategorie.length > 0 && (
          <ul className="divide-y rounded-surface border">
            {sansCategorie.map(ligne)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Mêmes paliers que `PamiMatchingChart` (poids 1 / 0,5 / 0, le 0,5 n'existant
 *  que dans les référentiels historiques) : ce qui pèse s'impose par le
 *  contraste, ce qui pèse peu s'efface sans disparaître. */
function opaciteImportance(poids: number | undefined): number {
  if (poids === undefined) return 0.5;
  if (poids >= 1) return 0.9;
  if (poids >= 0.5) return 0.55;
  return 0.28;
}
